import { useForm } from "@inertiajs/react";
import {
  Children,
  cloneElement,
  type FormEvent,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

import Button from "@/components/Button";
import LinkButton from "@/components/LinkButton";
import SearchableSelect from "@/components/SearchableSelect";
import { categories } from "@/routes";
import type { Category } from "@/types";

const STATUS_LABELS: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
};

const COURSE_TYPE_LABELS: Record<string, string> = {
  retirement_course: "Curso de retiro",
  coexistence: "Convivencia",
  other: "Otro",
  yearly: "Curso Anual",
  jornada: "Jornada",
};

const FIELD_INPUT_CLASSES =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink shadow-none placeholder:text-ink/40 focus:outline-none focus:ring-0 focus:shadow-none";

function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-4 rounded-2xl border-2 border-ink/20 p-5">
      <legend className="flex items-center gap-2 px-2 font-sans text-sm font-semibold text-ink">
        <span className="text-steel">{icon}</span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

/** Labels a single input/select the same way `Section` labels a card: the
 * name sits on the border as a legend once the field has a value, so a
 * filled field stays identifiable. While empty, only the placeholder (also
 * derived from `label`) shows, so the two don't display redundantly. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  const child = Children.only(children);
  const childProps = isValidElement(child)
    ? (child.props as { value?: unknown; required?: boolean })
    : undefined;
  const hasValue =
    childProps?.value !== undefined &&
    childProps?.value !== null &&
    childProps?.value !== "";
  const invalid = !!childProps?.required && !hasValue;
  const displayLabel = childProps?.required ? `${label} *` : label;
  const showLegend = hasValue || invalid;

  const content = isValidElement(child)
    ? cloneElement(child as ReactElement<{ placeholder?: string }>, {
        placeholder:
          (child.props as { placeholder?: string }).placeholder ?? displayLabel,
      })
    : child;

  return (
    <fieldset
      className={`min-w-0 rounded-full border-2 px-4 focus-within:border-steel ${showLegend ? "pb-2.5" : "py-3.5"} ${invalid ? "border-deep-red" : "border-ink"}`}
    >
      {showLegend && (
        <legend
          className={`truncate px-1.5 font-sans text-xs ${invalid ? "text-deep-red" : "text-ink/60"}`}
        >
          {displayLabel}
        </legend>
      )}
      {content}
    </fieldset>
  );
}

function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={FIELD_INPUT_CLASSES} {...props} />;
}

type Props = {
  data: Category;
};

export default function CategoriesEdit({ data: category }: Props) {
  const form = useForm({
    name: category.name ?? "",
    status: category.status ?? "",
    course_type: category.course_type ?? "",
  });

  const isNew = !category.id;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!e.currentTarget.checkValidity()) {
      e.currentTarget.reportValidity();
      return;
    }
    form.transform((data) => ({ category: data }));
    if (isNew) {
      form.post(categories.create().url);
    } else {
      form.patch(categories.update(category.id).url);
    }
  }

  return (
    <div className="mx-auto w-[90%] max-w-2xl py-10">
      <h1 className="mb-1 font-sans text-3xl font-semibold text-ink">
        {isNew ? "Nuevo tipo de curso" : category.name || "Tipo de curso"}
      </h1>
      <p className="mb-6 font-sans text-sm text-ink/60">
        Los campos marcados con * son obligatorios.
      </p>

      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Section icon={<TagIcon />} title="Datos generales">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre">
              <Input
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                required
              />
            </Field>
            <Field label="Estado">
              <SearchableSelect
                value={form.data.status}
                onChange={(v) => form.setData("status", v)}
                options={Object.entries(STATUS_LABELS).map(
                  ([value, label]) => ({ value, label }),
                )}
              />
            </Field>
            <Field label="Tipo de curso">
              <SearchableSelect
                value={form.data.course_type}
                onChange={(v) => form.setData("course_type", v)}
                options={Object.entries(COURSE_TYPE_LABELS).map(
                  ([value, label]) => ({ value, label }),
                )}
              />
            </Field>
          </div>
        </Section>

        <div className="mt-2 flex gap-3">
          <Button type="submit" disabled={form.processing}>
            Guardar
          </Button>
          <LinkButton href={categories.index().url}>Cancelar</LinkButton>
        </div>
      </form>
    </div>
  );
}

function TagIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 3h6a2 2 0 012 2v6l-9.5 9.5a2 2 0 01-2.83 0l-5.17-5.17a2 2 0 010-2.83L11 3z"
      />
      <circle cx="15.5" cy="8.5" r="1.25" fill="currentColor" stroke="none" />
    </svg>
  );
}
